/**
 * Catalogue, validation et mémoire des FONDS DE CARTE — logique PURE (aucune
 * dépendance à Leaflet ni au DOM) : c'est ce qui se teste.
 *
 * Copie IDENTIQUE dans les trois consoles (portail SaaS et console opérateur
 * SwiftDeliver, dispatch SwiftEats) ; seule la constante `PREFIXE_STOCKAGE` de
 * `fondDeCarte.ts` change d'une console à l'autre. Voir
 * `SwiftDeliver/docs/FONDS-DE-CARTE.md`.
 *
 * ⚠️ LICENCES. Ne figurent au catalogue que des fonds dont l'usage commercial
 * est autorisé SANS clé : OpenFreeMap et OpenStreetMap / OpenTopoMap (avec leur
 * attribution). Ni Google (tuiles non officielles : contraire à leurs
 * conditions), ni Bing, ni Esri, ni CARTO : tous exigent une clé ou limitent
 * l'usage commercial.
 */

/** `vecteur` : style MapLibre (adresse d'un style JSON). `raster` : tuiles PNG `{z}/{x}/{y}`. */
export type TypeFond = 'vecteur' | 'raster'

export interface FondDeCarte {
  id: string
  nom: string
  type: TypeFond
  /** Vecteur : adresse du style. Raster : modèle d'adresse des tuiles. */
  url: string
  /**
   * HTML d'attribution, DÉJÀ SÛR : écrit à la main pour le catalogue, ÉCHAPPÉ
   * (texte simple) pour un fond ajouté par l'utilisateur.
   */
  attributionHtml: string
  /** Raster : plus haut niveau de zoom où le serveur a des tuiles (au-delà, on agrandit). */
  zoomMax?: number
  /** Ajouté par l'utilisateur (donc retirable). */
  perso: boolean
  /** Fond ajouté : l'attribution telle que saisie (TEXTE, avant échappement) — c'est elle qu'on mémorise. */
  attributionTexte?: string
}

/** Ce qui est écrit dans le navigateur pour un fond ajouté (l'attribution reste du TEXTE). */
export interface FondPersoStocke {
  id: string
  nom: string
  url: string
  attribution: string
  zoomMax?: number
}

/** Champ du formulaire en cause, pour que l'interface y place le curseur. */
export type ChampFond = 'nom' | 'url' | 'attribution' | 'zoomMax'
export type Resultat<T> = { ok: true; valeur: T } | { ok: false; erreur: string; champ?: ChampFond }

export const ID_FOND_DEFAUT = 'liberty'
/** Zoom maximal de la carte elle-même (voir `OPTIONS_CARTE`) : un raster plus pauvre est agrandi. */
export const ZOOM_MAX_CARTE = 19
export const MAX_FONDS_PERSO = 12
const LONGUEUR_MAX_NOM = 40
const LONGUEUR_MAX_ATTRIBUTION = 200
const LONGUEUR_MAX_URL = 500

const OFM = '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a>'
const OMT = '<a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a>'
const OSM = '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'

/**
 * ⚠️ L'attribution OpenFreeMap / OpenMapTiles / OpenStreetMap est une CONDITION
 * d'usage, pas une politesse : elle reste lisible sur la carte.
 */
export const ATTRIBUTION_OPENFREEMAP = `${OFM} &copy; ${OMT} Data from ${OSM}`

const ofm = (id: string, nom: string): FondDeCarte => ({
  id,
  nom,
  type: 'vecteur',
  url: `https://tiles.openfreemap.org/styles/${id}`,
  attributionHtml: ATTRIBUTION_OPENFREEMAP,
  perso: false,
})

/** Le catalogue livré. `liberty` est le DÉFAUT (premier de la liste). */
export const FONDS_INTEGRES: readonly FondDeCarte[] = [
  ofm('liberty', 'Détaillé'),
  ofm('bright', 'Clair'),
  ofm('positron', 'Épuré'),
  ofm('dark', 'Sombre'),
  {
    id: 'osm',
    nom: 'OpenStreetMap',
    type: 'raster',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attributionHtml: `&copy; ${OSM} contributors`,
    zoomMax: 19,
    perso: false,
  },
  {
    id: 'relief',
    nom: 'Relief',
    type: 'raster',
    url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
    attributionHtml:
      `Données &copy; ${OSM} contributors, SRTM · style &copy; ` +
      '<a href="https://opentopomap.org" target="_blank" rel="noopener">OpenTopoMap</a> ' +
      '(<a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener">CC-BY-SA</a>)',
    zoomMax: 17,
    perso: false,
  },
]

/** Échappe un TEXTE pour l'insérer dans du HTML (l'attribution d'un fond ajouté). */
export function echapperHtml(texte: string): string {
  return texte.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c)
}

/* ───────────────────────── Validation ───────────────────────── */

const ERR = <T>(erreur: string, champ?: ChampFond): Resultat<T> => ({ ok: false, erreur, ...(champ ? { champ } : {}) })

/** Paramètres compris par Leaflet pour une adresse de tuiles ; `{s}` = sous-domaines a, b, c. */
const PARAMETRES_REQUIS = ['z', 'x', 'y'] as const
const PARAMETRES_ADMIS = new Set<string>([...PARAMETRES_REQUIS, 's'])

/**
 * Valide l'adresse d'un fond raster. Règles, de la plus grave à la plus fine :
 *  - `https://` OBLIGATOIRE (`http:` refusé : une console en https ne charge pas
 *    de tuiles http ; `javascript:`, `data:`, `ftp:`… refusés) ;
 *  - ni espace ni caractère de contrôle, ni identifiants (`https://nom:mdp@…`) ;
 *  - `{z}`, `{x}` et `{y}` présents après le nom d'hôte ; `{s}` toléré ; tout
 *    autre `{paramètre}` refusé (Leaflet lèverait une erreur au premier tuilage).
 */
export function validerUrlTuiles(brut: string): Resultat<string> {
  const url = brut.trim()
  if (!url) return ERR("Indiquez l'adresse des tuiles.", 'url')
  if (url.length > LONGUEUR_MAX_URL) return ERR(`Adresse trop longue (${LONGUEUR_MAX_URL} caractères au plus).`, 'url')
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(url)) return ERR("L'adresse ne doit contenir ni espace ni retour à la ligne.", 'url')

  const protocole = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1]?.toLowerCase()
  if (!protocole) return ERR("L'adresse doit commencer par https://", 'url')
  if (protocole === 'http') return ERR('http:// est refusé : utilisez https:// (le navigateur bloque les tuiles non sécurisées).', 'url')
  if (protocole !== 'https') return ERR(`Le protocole « ${protocole}: » est refusé : seule une adresse https:// est acceptée.`, 'url')
  if (!/^https:\/\//i.test(url)) return ERR("L'adresse doit commencer par https://", 'url')

  const apresProtocole = url.slice('https://'.length)
  const finHote = apresProtocole.search(/[/?#]/)
  const hote = finHote === -1 ? apresProtocole : apresProtocole.slice(0, finHote)
  const reste = finHote === -1 ? '' : apresProtocole.slice(finHote)
  if (!hote) return ERR("Le nom du serveur manque après https://", 'url')
  if (hote.includes('@')) return ERR("L'adresse ne doit pas contenir d'identifiants (nom:mot de passe@).", 'url')

  return validerAccolades(url, hote, reste)
}

function validerAccolades(url: string, hote: string, reste: string): Resultat<string> {
  const jetons = [...url.matchAll(/\{([^{}]*)\}/g)].map((m) => m[1] ?? '')
  const inconnu = jetons.find((j) => !PARAMETRES_ADMIS.has(j))
  if (inconnu !== undefined) {
    return ERR(`Paramètre {${inconnu}} inconnu : seuls {z}, {x} et {y} (et {s} pour les sous-domaines) sont admis.`, 'url')
  }
  // Accolade isolée : `{z` ou `x}` — Leaflet la laisserait telle quelle dans la requête.
  if (url.replace(/\{[^{}]*\}/g, '').match(/[{}]/)) return ERR('Accolade isolée : écrivez les paramètres exactement {z}, {x} et {y}.', 'url')

  if (/\{[zxy]\}/.test(hote)) return ERR('{z}, {x} et {y} vont après le nom du serveur, pas dedans.', 'url')
  const manquants = PARAMETRES_REQUIS.filter((p) => !reste.includes(`{${p}}`))
  if (manquants.length > 0) {
    return ERR(`Il manque ${manquants.map((p) => `{${p}}`).join(', ')} dans l'adresse (exemple : https://serveur/{z}/{x}/{y}.png).`, 'url')
  }

  // Dernier garde-fou : l'adresse, une fois les paramètres remplacés, doit être lisible par le navigateur.
  try {
    const essai = new URL(url.replace(/\{s\}/g, 'a').replace(/\{[zxy]\}/g, '1'))
    if (essai.protocol !== 'https:' || !essai.hostname) return ERR('Adresse illisible : vérifiez le nom du serveur.', 'url')
    if (essai.username || essai.password) return ERR("L'adresse ne doit pas contenir d'identifiants (nom:mot de passe@).", 'url')
  } catch {
    return ERR('Adresse illisible : vérifiez le nom du serveur.', 'url')
  }
  return { ok: true, valeur: url }
}

export interface SaisieFond {
  nom: string
  url: string
  attribution: string
  /** Texte libre du champ « zoom max » ; vide = non précisé. */
  zoomMax?: string | undefined
}

/** Valide les champs du formulaire (sans l'identifiant ni l'unicité du nom). */
function validerChamps(saisie: SaisieFond): Resultat<Omit<FondPersoStocke, 'id'>> {
  const nom = saisie.nom.trim().replace(/\s+/g, ' ')
  if (!nom) return ERR('Donnez un nom à ce fond.', 'nom')
  if (nom.length > LONGUEUR_MAX_NOM) return ERR(`Nom trop long (${LONGUEUR_MAX_NOM} caractères au plus).`, 'nom')

  const url = validerUrlTuiles(saisie.url)
  if (!url.ok) return url

  const attribution = saisie.attribution.trim().replace(/\s+/g, ' ')
  if (!attribution) return ERR("L'attribution est obligatoire : citez la source de la carte (exemple : © OpenStreetMap contributors).", 'attribution')
  if (attribution.length > LONGUEUR_MAX_ATTRIBUTION) return ERR(`Attribution trop longue (${LONGUEUR_MAX_ATTRIBUTION} caractères au plus).`, 'attribution')

  const brutZoom = (saisie.zoomMax ?? '').trim()
  let zoomMax: number | undefined
  if (brutZoom) {
    if (!/^\d{1,2}$/.test(brutZoom) || Number(brutZoom) < 1 || Number(brutZoom) > 22) {
      return ERR('Zoom max : un nombre entier entre 1 et 22, ou laissez vide.', 'zoomMax')
    }
    zoomMax = Number(brutZoom)
  }
  return { ok: true, valeur: { nom, url: url.valeur, attribution, ...(zoomMax !== undefined ? { zoomMax } : {}) } }
}

/** Fond affichable à partir de ce qui est stocké : l'attribution (TEXTE) est ÉCHAPPÉE ici. */
export function fondDepuisStocke(s: FondPersoStocke): FondDeCarte {
  return {
    id: s.id,
    nom: s.nom,
    type: 'raster',
    url: s.url,
    attributionHtml: echapperHtml(s.attribution),
    zoomMax: s.zoomMax ?? ZOOM_MAX_CARTE,
    perso: true,
    attributionTexte: s.attribution,
  }
}

/** Relit un fond stocké : tout ce qui n'est pas valide est écarté (jamais d'erreur). */
function relireStocke(v: unknown): FondPersoStocke | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.id !== 'string' || !/^perso-[a-z0-9-]{1,40}$/.test(o.id)) return null
  if (typeof o.nom !== 'string' || typeof o.url !== 'string' || typeof o.attribution !== 'string') return null
  const valide = validerChamps({
    nom: o.nom,
    url: o.url,
    attribution: o.attribution,
    zoomMax: typeof o.zoomMax === 'number' ? String(o.zoomMax) : undefined,
  })
  return valide.ok ? { id: o.id, ...valide.valeur } : null
}

/* ───────────────────────── Mémoire ───────────────────────── */

/** Le strict nécessaire de `Storage` : un faux objet suffit pour les tests. */
export interface StockageMinimal {
  getItem(cle: string): string | null
  setItem(cle: string, valeur: string): void
}

export interface EtatFonds {
  actifId: string
  perso: readonly FondDeCarte[]
}

export interface MagasinFonds {
  /** Tous les fonds : le catalogue puis ceux de l'utilisateur. */
  fonds(): readonly FondDeCarte[]
  actif(): FondDeCarte
  /** Choisit un fond ; `false` si l'identifiant est inconnu (rien ne change). */
  choisir(id: string): boolean
  /** Ajoute (et choisit) un fond. */
  ajouter(saisie: SaisieFond): Resultat<FondDeCarte>
  /** Retire un fond AJOUTÉ ; si c'était le fond actif, retombe sur `liberty`. Le catalogue ne se retire pas. */
  retirer(id: string): boolean
  /** Appelé à chaque changement ; renvoie la fonction qui se désabonne. */
  abonner(rappel: () => void): () => void
  /** Relit le stockage (autre onglet qui a changé le choix). */
  recharger(): void
  /** Faux si le navigateur refuse le stockage : le choix tient alors le temps de la session seulement. */
  stockageDisponible(): boolean
}

export interface OptionsMagasin {
  /** Préfixe de console des clés de stockage (ex. `sd_portail`). */
  prefixe: string
  /** Fournit le stockage ; peut renvoyer `null` ou lever (mode privé, stockage bloqué). */
  stockage?: () => StockageMinimal | null
  /** Fabrique d'identifiants (tests). */
  genererId?: () => string
}

export function cleStockage(prefixe: string): { actif: string; perso: string } {
  return { actif: `${prefixe}_fond_carte_actif`, perso: `${prefixe}_fond_carte_perso` }
}

function stockageNavigateur(): StockageMinimal | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

const idAleatoire = () => `perso-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export function creerMagasinFonds(options: OptionsMagasin): MagasinFonds {
  const cles = cleStockage(options.prefixe)
  const fournir = options.stockage ?? stockageNavigateur
  const genererId = options.genererId ?? idAleatoire
  const abonnes = new Set<() => void>()
  let disponible = true

  /** Lecture protégée : jamais d'exception, `null` si indisponible. */
  const lire = (cle: string): string | null => {
    try {
      const s = fournir()
      if (!s) {
        disponible = false
        return null
      }
      return s.getItem(cle)
    } catch {
      disponible = false
      return null
    }
  }
  const ecrire = (cle: string, valeur: string): void => {
    try {
      const s = fournir()
      if (!s) {
        disponible = false
        return
      }
      s.setItem(cle, valeur)
    } catch {
      disponible = false
    }
  }

  const lirePerso = (): FondDeCarte[] => {
    const brut = lire(cles.perso)
    if (!brut) return []
    let tableau: unknown
    try {
      tableau = JSON.parse(brut)
    } catch {
      return []
    }
    if (!Array.isArray(tableau)) return []
    const vus = new Set<string>()
    const fonds: FondDeCarte[] = []
    for (const v of tableau) {
      const s = relireStocke(v)
      if (!s || vus.has(s.id)) continue
      vus.add(s.id)
      fonds.push(fondDepuisStocke(s))
      if (fonds.length >= MAX_FONDS_PERSO) break
    }
    return fonds
  }

  const choisirValide = (id: string | null, perso: readonly FondDeCarte[]): string =>
    id && (FONDS_INTEGRES.some((f) => f.id === id) || perso.some((f) => f.id === id)) ? id : ID_FOND_DEFAUT

  const charger = (): EtatFonds => {
    const perso = lirePerso()
    return { actifId: choisirValide(lire(cles.actif), perso), perso }
  }

  let etat: EtatFonds = charger()

  const notifier = () => abonnes.forEach((rappel) => rappel())
  const persisterActif = () => ecrire(cles.actif, etat.actifId)
  const persisterPerso = () =>
    ecrire(
      cles.perso,
      JSON.stringify(
        etat.perso.map(
          (f): FondPersoStocke => ({
            id: f.id,
            nom: f.nom,
            url: f.url,
            attribution: f.attributionTexte ?? '',
            ...(f.zoomMax !== undefined && f.zoomMax !== ZOOM_MAX_CARTE ? { zoomMax: f.zoomMax } : {}),
          }),
        ),
      ),
    )

  const tous = (): readonly FondDeCarte[] => [...FONDS_INTEGRES, ...etat.perso]

  return {
    fonds: tous,
    actif: () => tous().find((f) => f.id === etat.actifId) ?? FONDS_INTEGRES[0]!,
    choisir(id) {
      if (!tous().some((f) => f.id === id)) return false
      if (id === etat.actifId) return true
      etat = { ...etat, actifId: id }
      persisterActif()
      notifier()
      return true
    },
    ajouter(saisie) {
      if (etat.perso.length >= MAX_FONDS_PERSO) return ERR(`Vous avez déjà ${MAX_FONDS_PERSO} fonds ajoutés : retirez-en un d'abord.`)
      const champs = validerChamps(saisie)
      if (!champs.ok) return champs
      const nomLower = champs.valeur.nom.toLocaleLowerCase('fr')
      if (tous().some((f) => f.nom.toLocaleLowerCase('fr') === nomLower)) return ERR('Un fond porte déjà ce nom : choisissez-en un autre.', 'nom')
      let id = genererId()
      while (tous().some((f) => f.id === id)) id = genererId()
      const fond = fondDepuisStocke({ id, ...champs.valeur })
      etat = { actifId: fond.id, perso: [...etat.perso, fond] }
      persisterPerso()
      persisterActif()
      notifier()
      return { ok: true, valeur: fond }
    },
    retirer(id) {
      if (!etat.perso.some((f) => f.id === id)) return false
      etat = { actifId: etat.actifId === id ? ID_FOND_DEFAUT : etat.actifId, perso: etat.perso.filter((f) => f.id !== id) }
      persisterPerso()
      persisterActif()
      notifier()
      return true
    },
    abonner(rappel) {
      abonnes.add(rappel)
      return () => {
        abonnes.delete(rappel)
      }
    },
    recharger() {
      const apres = charger()
      const identique =
        apres.actifId === etat.actifId &&
        apres.perso.length === etat.perso.length &&
        apres.perso.every((f, i) => f.id === etat.perso[i]?.id && f.url === etat.perso[i]?.url)
      if (identique) return
      etat = apres
      notifier()
    },
    stockageDisponible: () => disponible,
  }
}
